import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { CommonModule } from '@angular/common';
import { AssessmentSituationComponent } from './assessment-situation/assessment-situation.component';
import { EvaluationAdditionalInfoComponent } from "./evaluation-additional-info/evaluation-additional-info.component";
import { RequiredAssessmentsComponent } from "./required-assessments/required-assessments.component";
import { OfficialSignatureComponent } from "./official-signature/official-signature.component";
import { EvaluationSubmissionComponent } from "./evaluation-submission/evaluation-submission.component";
import { ActivatedRoute, Router } from '@angular/router';
import { StudentInfoComponent } from "../../components/student-info/student-info.component";
import { AssessCaseService } from '../../../shared/services/assessCase.service';
import { AssessScreenService } from '../../../shared/services/assessScreen.service';
import { IAssessScreen } from '../../../shared/models/assessScreen';
import { EYesNo } from '../../../shared/models/types.enum';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { StudentService } from '../../../shared/services/student.service';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'evaluation-form',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    AssessmentSituationComponent,
    EvaluationAdditionalInfoComponent,
    RequiredAssessmentsComponent,
    OfficialSignatureComponent,
    EvaluationSubmissionComponent,
    StudentInfoComponent,
    SpinnerComponent,
    PopupMessageComponent
],
  templateUrl: './evaluation-form.component.html',
  styleUrl: './evaluation-form.component.css'
})
export class EvaluationFormComponent implements OnInit {

  icons = Icons;
  yesNo = EYesNo;
  currentCaseId: string | null = null;
  currentBuildingId: string | null = null;
  regionId: string | null = null;
  studentSSID: string = '';
  studentInfo: any | null = null;
  currentAssessScreen: any | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  stepsConfig = [
    {
      id: 1,
      completionProperty: 'threat_assessment_situation_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'additional_information_completion',
      nextStepDependency: 'threat_assessment_situation_completion'
    },
    {
      id: 3,
      completionProperty: 'required_assessments_completion',
      nextStepDependency: 'additional_information_completion'
    },
    {
      id: 4,
      completionProperty: 'official_signatures_completion',
      nextStepDependency: 'required_assessments_completion'
    }
  ];
  finalStepConfig = {
      id: 5,
      completionProperty: 'assessment_screen_completion',
      nextStepDependency: 'official_signatures_completion'
  };
  tooltips = ['Threat Assessment Situation', 'Additional Information', 'Required Assessments', 'Official Signatures', 'Submission'];
  currentStep = 1;

  showStudentInfo: boolean = false;
  loading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private route: ActivatedRoute,
    private assessCaseService: AssessCaseService,
    private assessScreenService: AssessScreenService,
    private studentService: StudentService,
    private userDataService: UserDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.regionId = this.userDataService.getRegionId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    this.getAssessScreen();
  }

  getAssessScreen(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.assessScreenService.getAssessScreenByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (response) {
            this.currentAssessScreen = response;
          } else {
            this.currentAssessScreen = null;
            this.messageModal = 'unsuccess';
            this.popupMessage = 'Assess Screen not found';
          }
          this.loading = false;
          return
        },
        error: (error) => {
          console.error('Error fetching assess screen:', error);
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleOpenModal(type: string): void {
    if (type == 'student info') {
      if (this.currentCaseId) {
        // find student id by case id
        this.assessCaseService.getAssessCaseByCaseId(this.currentCaseId).subscribe({
          next: (response) => {
            if (!response || !response.student_identifier) {
              this.messageModal = 'unsuccess';
              this.popupMessage = 'No student identifier found for this case.';
              return;
            }
            this.studentSSID = response.student_identifier;
            // get student info by student id
            this.getStudentInfo();
          },
          error: (error) => {
            console.error('Error fetching assess case:', error);
          }
        });

      }
    }
  }

  getStudentInfo(): void {
    if (this.studentSSID && this.currentBuildingId && this.regionId) {
      this.loading = true;
      this.studentService.findStudentBySsid(this.regionId, {building_id: this.currentBuildingId, student_identifier: this.studentSSID}).subscribe({
        next: (res) => {
          this.loading = false;
          this.showStudentInfo = true;
          this.studentInfo = res.student;
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
        }
      });
    }
  }


  handleCloseModal(type: string): void {
    if (type == 'student info') {
      this.showStudentInfo = false;
    }
  }

  handleUpdateAssessScreen(data: IAssessScreen, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);

      this.assessScreenService.updateAssessScreen(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.toastr.success('Saved successful!', 'Success');
          this.getAssessScreen();
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 5 ) {
            this.router.navigate(['/evaluations/']);
          }
        },
        error: (error) => {
          this.loading = false;
          this.toastr.error(error.message, 'Error');
          console.error('Error updating assess screen:', error.message);
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/evaluations/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }

    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }

    if (stepConfig.nextStepDependency && this.currentAssessScreen) {
      return this.currentAssessScreen?.[stepConfig.nextStepDependency] === 1;
    }

    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
